/** @jsxRuntime classic */
/** @jsx jsx */
import { useEffect, useState, useCallback } from 'react';
import { css, jsx } from '@emotion/react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { Typography as Text } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PasswordField from '../components/common/PasswordField';
import { Link as RouterLink } from 'react-router-dom';

import { login } from '../actions/UserActions';
import { useStore, TOKEN, addSnack } from '../store';
import { Navigate } from 'react-router-dom';
import Divider from '@mui/material/Divider';
import { useQueryClient } from 'react-query';

import Skeleton from '../components/Skeleton';
import { useIsMobile } from '../utils/hooks';

import GoogleSignInButton from '../components/login/GoogleSignInButton';
import MicrosoftSignInButton from '../components/login/MicrosoftSignInButton';
import BankIDSignInButton from '../components/login/BankIDSignInButton';
import CustomIdpSignInButton from '../components/login/CustomIdpSignInButton';

import { useQuery } from '../utils/query';
import { IS_SINGLE_TENENT_MODE, CUSTOM_IDP } from '../constants';

function getErrorMessage(errorCode, t) {
  if (errorCode === 'invalid_credentials')
    return t('Fel användarnamn eller lösenord');
  if (errorCode === 'invalid_otp') return t('Felaktig kod');
  if (errorCode === 'invalid_token') return t('Ogiltig token');
  if (errorCode === 'no_bankid') {
    return t(
      'Inget konto är kopplat till ditt BankID. Premiumanvändare kan aktivera BankID under inställningarna på sitt konto.'
    );
  }
  if (errorCode === 'IDP-3200') {
    return t(
      'Inloggningen avbröts. Vänligen försök igen eller kontakta support.'
    );
  }
  if (errorCode === 'no_invite_token') {
    return t(
      'Denna mailadress har blivit inbjuden till en arbetsyta. Vänligen kolla din email för en inbjudningslänk.'
    );
  }

  if (errorCode === 'invalid_invite_token') {
    return t(
      'Denna mejlen är knuten till en anna inloggningslänk. Vänligen kontrollera att du valde rätt email adress och länk'
    );
  }

  return t('Ett fel uppstod');
}

export default function Login({ redirect = true }) {
  const { t } = useTranslation();
  const [
    { token: _token, otp: _otp, error: _error, secure_login: _secureLogin },
    setQuery,
  ] = useQuery();
  const { isMobile, width } = useIsMobile();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [, setToken] = useStore(TOKEN);
  const [error, setError] = useState(_error ? getErrorMessage(_error, t) : '');
  const [ssoError, setSsoError] = useState('');
  const [success, setSuccess] = useState(false);
  const [otp, setOtp] = useState(false);
  const [secureLogin, setSecureLogin] = useState(false);
  const [showAlternate, setShowAlternate] = useState(false);

  const queryClient = useQueryClient();
  useEffect(() => {
    if (_token) {
      (async () => {
        setQuery({});
        setToken(_token);
        setOtp(_otp);
        setSuccess(true);
        setSecureLogin(_secureLogin);
      })();
    }
  }, [_token, _otp, _secureLogin, setQuery, setToken]);

  async function handleLogin(e) {
    e.preventDefault();
    try {
      const res = await login(email, password);
      setToken(res?.token);
      queryClient.setQueryData('self', res?.user);
      setSuccess(true);
      addSnack({ msg: t('Du loggas in'), variant: 'success', duration: 3000 });
      setOtp(res?.otp);
      setSecureLogin(res?.secure_login);
    } catch (err) {
      if (err?.response?.status === 401) {
        return setError(t('Fel email eller lösenord'));
      }

      if (err?.response?.status === 429) {
        return setError(
          t(
            'Du har gjort för många inloggningsförsök på kort tid. Vänligen vänta en stund och försök igen'
          )
        );
      }
      setError(t('Internt fel. Var god försök igen'));
    }
  }

  const handleSsoError = useCallback(
    (err) => {
      if (err?.response?.data?.msg === 'email already in use') {
        setSsoError(
          t(
            'Email redan i bruk. Var god testa att logga in med något annat alternativ'
          )
        );
      } else if (err?.response?.data?.msg === 'No invite token') {
        setSsoError(
          t(
            'Detta kontot är inbjuden med en länk. För att skapa ett konto, vänligen kontrollera din mejl för en länk'
          )
        );
      } else if (err?.response?.data?.msg === 'Invalid invite token') {
        setSsoError(
          t(
            'Detta kontot är knuten till en inbjudnings länk, vänligen kontrollera att rätt länk och email adress har använts'
          )
        );
      } else {
        setSsoError(t('Internt fel. Var god försök igen'));
      }
    },
    [t]
  );

  if (success && redirect) {
    if (otp) {
      return <Navigate to="/2fa-verify" />;
    }
    if (secureLogin) {
      return <Navigate to="/secure-login/verify" />;
    }
    return <Navigate to="/" />;
  }

  if (IS_SINGLE_TENENT_MODE && CUSTOM_IDP && !showAlternate) {
    return (
      <Skeleton
        publicPage
        title={t('Klang | Logga in')}
        showLanguageSelector={false}
        showLoginLinks
        fetchUser={false}
      >
        <div
          css={css`
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              width: 100%;
              max-width: 25rem;
              margin-top: 5rem;
              padding: 1rem;
              gap: 1rem;
            `}
          >
            <Text variant="h4" fontWeight="bold" css={css``}>
              {t('Logga in på Klang.ai')}
            </Text>
            <CustomIdpSignInButton customIdp={CUSTOM_IDP} />
            <Button
              variant="text"
              color="primary"
              css={css`
                margin-top: 1rem;
              `}
              onClick={() => setShowAlternate(true)}
            >
              {t('Logga in med email och lösenord')}
            </Button>
            {error && (
              <Text
                color="error"
                css={css`
                  margin-bottom: 1rem;
                  text-align: center;
                `}
              >
                {error}
              </Text>
            )}
          </div>
        </div>
      </Skeleton>
    );
  }

  return (
    <Skeleton
      publicPage
      title={t('Klang | Logga in')}
      showLanguageSelector={false}
      showLoginLinks
      fetchUser={false}
    >
      <div
        css={css`
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            max-width: 25rem;
            margin-top: 5rem;
            padding: 1rem;
            gap: 1rem;
          `}
        >
          <Text variant="h4" fontWeight="bold" css={css``}>
            {t('Logga in på Klang.ai')}
          </Text>
          {showAlternate && CUSTOM_IDP ? (
            <Button
              onClick={() => setShowAlternate(false)}
              variant="text"
              color="primary"
              css={css`
                margin-bottom: 1rem;
              `}
            >
              {t('Logga in med {{name}}', {
                name: CUSTOM_IDP.name,
              })}
            </Button>
          ) : null}
          {!IS_SINGLE_TENENT_MODE && (
            <div
              css={css`
                display: flex;
                flex-direction: row;
                align-items: center;
              `}
            >
              <Text variant="body1">{t('Har du inget konto?')}</Text>
              <RouterLink
                to="/signup"
                css={css`
                  margin-left: 0.5rem;
                  text-decoration: none;
                  color: var(--primary-main);
                `}
              >
                <Text variant="body1" fontWeight="bold">
                  {t('Skapa ett här')}
                </Text>
              </RouterLink>
            </div>
          )}

          <form
            onSubmit={handleLogin}
            css={css`
              display: flex;
              flex-direction: column;
              width: 100%;
            `}
          >
            <TextField
              label={t('Email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              css={css`
                margin-top: 2rem;
                margin-bottom: 1rem;
              `}
              error={!!error}
              autoComplete="off"
              autoFocus
            />
            <PasswordField
              label={t('Lösenord')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!error}
              helperText={error || ' '}
            />
            <Button
              variant="contained"
              color="primary"
              css={css`
                margin-top: 0.5rem;
              `}
              size="large"
              type="submit"
              disabled={!email || !password}
            >
              {t('Logga in')}
            </Button>
            <RouterLink
              to="/forgot-password"
              css={css`
                margin-top: 1rem;
                text-decoration: none;
                color: var(--primary-main);
                text-align: center;
              `}
            >
              <Text variant="body1" fontWeight="600">
                {t('Återställ lösenord')}
              </Text>
            </RouterLink>
          </form>
          {!IS_SINGLE_TENENT_MODE && (
            <Divider
              css={css`
                margin-top: 1rem;
                margin-bottom: 1rem;
              `}
              flexItem
            >
              <Text variant="body1" fontWeight="600">
                {t('Eller')}
              </Text>
            </Divider>
          )}

          {!!ssoError && (
            <Text
              color="error"
              css={css`
                margin-bottom: 1rem;
                text-align: center;
              `}
            >
              {ssoError}
            </Text>
          )}
          {!IS_SINGLE_TENENT_MODE && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 1rem;
              `}
            >
              <GoogleSignInButton
                onError={handleSsoError}
                width={isMobile ? width - 64 : 395}
              />
              <MicrosoftSignInButton width={isMobile ? width - 64 : 395} />
              <BankIDSignInButton width={isMobile ? width - 64 : 395} />
            </div>
          )}
        </div>
      </div>
    </Skeleton>
  );
}
